import React, { useState, useContext } from "react";
import { Container, Row, Col, Card, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Button from "../button/Button";
import MyContext from "../../../context/MyContext";

// role (from URL) → api prefix / login route / labels
const ROLE_CONFIG = {
  admin: {
    apiPrefix: "/admin/auth",
    loginRoute: "/admin/login",
    emailLabel: "admin email",
    emailPlaceholder: "admin@kisaanmart.com",
  },
  seller: {
    apiPrefix: "/seller/auth",
    loginRoute: "/seller/login",
    emailLabel: "seller email",
    emailPlaceholder: "Enter your seller email",
  },
  customer: {
    apiPrefix: "/customer/auth",
    loginRoute: "/customer/login",
    emailLabel: "email",
    emailPlaceholder: "Enter your email",
  },
  delivery: {
    apiPrefix: "/delivery/auth",
    loginRoute: "/delivery/login",
    emailLabel: "delivery partner email",
    emailPlaceholder: "Enter your email",
  },
};

const ForgotPassword = () => {
  const { role } = useParams();
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.customer;

  const { baseUrl, regExpChecker } = useContext(MyContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = enter email, 2 = enter OTP + new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  // ════════════════════════════════
  // API: sendOtp
  // (also reused by the "Resend Code" link — see resendOtp below)
  // ════════════════════════════════
  const sendOtp = ({ isResend = false } = {}) => {
    const emailError = regExpChecker("email", email);
    if (emailError) {
      Swal.fire("Error!", emailError, "error");
      return;
    }

    isResend ? setResending(true) : setLoading(true);

    const fd = new FormData();
    fd.append("email", email);

    axios
      .post(`${baseUrl}${config.apiPrefix}/sendOtp`, fd)
      .then(({ data }) => {
        isResend ? setResending(false) : setLoading(false);
        if (data.success) {
          Swal.fire(
            "Sent!",
            isResend ? "A new code has been sent to your email." : "A reset code has been sent to your email.",
            "success"
          );
          setStep(2);
        } else {
          Swal.fire("Error!", data.message, "error");
        }
      })
      .catch(() => {
        isResend ? setResending(false) : setLoading(false);
        Swal.fire("Error!", "Failed to send the code. Please try again.", "error");
      });
  };

  // ════════════════════════════════
  // Resend OTP — reuses sendOtp with isResend flag
  // ════════════════════════════════
  const resendOtp = () => {
    sendOtp({ isResend: true });
  };

  // ════════════════════════════════
  // API: verifyOtpAndResetPassword
  // ════════════════════════════════
  const resetPassword = () => {
    if (!otp || !newPassword || !confirmPassword) {
      Swal.fire("Error!", "Please fill in all fields.", "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      Swal.fire("Error!", "Passwords do not match.", "error");
      return;
    }
    const passwordError = regExpChecker("password", newPassword);
    if (passwordError) {
      Swal.fire("Error!", passwordError, "error");
      return;
    }

    setLoading(true);
    const fd = new FormData();
    fd.append("email", email);
    fd.append("otp", otp);
    fd.append("newPassword", newPassword);

    axios
      .post(`${baseUrl}${config.apiPrefix}/verifyOtpAndResetPassword`, fd)
      .then(({ data }) => {
        setLoading(false);
        if (data.success) {
          Swal.fire("Success!", "Your password has been reset successfully.", "success").then(() => {
            navigate(config.loginRoute);
          });
        } else {
          Swal.fire("Error!", data.message, "error");
        }
      })
      .catch(() => {
        setLoading(false);
        Swal.fire("Error!", "Failed to reset password. Please try again.", "error");
      });
  };

  return (
    <Container fluid style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f9fa" }}>
      <Row className="justify-content-center w-100">
        <Col xs={12} md={6} lg={4}>
          <Card style={{ padding: "2rem", borderRadius: "16px" }}>
            <h3 style={{ marginBottom: "0.5rem" }}>Forgot Password</h3>
            <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
              {step === 1
                ? `Enter your ${config.emailLabel} and we'll send you a reset code.`
                : "Enter the code sent to your email and choose a new password."}
            </p>

            {step === 1 ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder={config.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Form.Group>
                <Button variant="gradient" fullWidth disabled={loading} onClick={() => sendOtp()}>
                  {loading ? "Sending..." : "Send Reset Code"}
                </Button>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>OTP Code</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </Form.Group>
                <Button variant="gradient" fullWidth disabled={loading} onClick={resetPassword}>
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>

                <p style={{ textAlign: "center", marginTop: "0.9rem", fontSize: "0.875rem", color: "#555" }}>
                  Didn't receive the code?{" "}
                  <span
                    onClick={() => !resending && resendOtp()}
                    style={{
                      color: "#097d43",
                      fontWeight: 600,
                      cursor: resending ? "default" : "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    {resending ? "Resending..." : "Resend"}
                  </span>
                </p>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{ background: "none", border: "none", color: "#097d43", marginTop: "0.5rem", fontSize: "0.875rem" }}
                >
                  ← Use a different email
                </button>
              </>
            )}

            <button
              type="button"
              onClick={() => navigate(config.loginRoute)}
              style={{ background: "none", border: "none", color: "#6b7280", marginTop: "1rem", fontSize: "0.875rem" }}
            >
              Back to Login
            </button>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ForgotPassword;
